import { loadVehicles } from "@/utils/productSearch";
import {
  buildVehicleIndex,
  detectVehicle,
  vehicleFromVin,
  engineLabel
} from "@/utils/vehicleIntent";
import { isVin, decodeVin, decodedLabel } from "@/utils/vinDecode";

// Un modelo sin año ni motor ("npr") son decenas de versiones: se muestran
// estas y se le pide a Claude que acote
const MAX_OPTIONS = 30;

// Una opción de la tarjeta: un vehículo concreto con todos sus ids de
// aplicación (las regiones que no se ven, juntas; ver vehicleIntent.js)
const optionOf = (option, match) => ({
  key: option.key,
  ids: option.ids,
  label: `${option.make} ${option.model} ${option.year}`,
  engine: engineLabel(option),
  family: option.family ? option.family.family : null,
  image:
    option.family && option.family.imageUrl
      ? option.family.imageUrl
      : match.image
});

export default {
  definition: {
    name: "find_vehicle",
    description:
      "Reconoce un vehículo en lo que dijo el vendedor (modelo obligatorio; marca, año, motor y cilindrada opcionales, p. ej. 'isuzu npr hd 2006 4hk1 5.2') o en un VIN de 17 caracteres, y le muestra los vehículos que encajan en una tarjeta para que elija uno. El elegido filtra search_parts_by_text y marca qué piezas le quedan. Pasa sólo las palabras del vehículo, no la pieza.",
    strict: true,
    input_schema: {
      type: "object",
      properties: {
        query: {
          type: "string",
          description: "Las palabras del vehículo o el VIN"
        }
      },
      required: ["query"],
      additionalProperties: false
    }
  },

  async run({ query }, ctx) {
    const index = buildVehicleIndex(await loadVehicles(ctx.collection));
    const words = query.trim().split(/\s+/);
    const vinAt = words.findIndex(isVin);
    let decoded = null;
    let match;
    if (vinAt !== -1) {
      decoded = await decodeVin(words[vinAt]);
      match = vehicleFromVin(decoded, index, vinAt);
    } else {
      match = detectVehicle(query, index);
    }

    if (!match) {
      return {
        content: JSON.stringify({
          found: false,
          ...(decoded ? { vinDecodedAs: decodedLabel(decoded) } : {}),
          hint:
            "Ningún vehículo del catálogo encaja. Pide el modelo tal como se vende (NPR, NPR-HD, NQR, FRR...) o busca la pieza sin vehículo."
        })
      };
    }

    const options = match.options
      .slice(0, MAX_OPTIONS)
      .map(option => optionOf(option, match));
    const truncated = match.options.length > options.length;
    return {
      card: {
        type: "vehicleOptions",
        title: match.label,
        detail: match.detail,
        total: match.options.length,
        options
      },
      content: JSON.stringify({
        shownToUserAsCard: true,
        matched: match.label,
        ...(decoded ? { vinDecodedAs: decodedLabel(decoded) } : {}),
        total: match.options.length,
        // Con uno solo la tarjeta ya lo deja elegido
        selected: match.options.length === 1 ? options[0].label : null,
        options: options.map(option => ({
          vehicle: option.label,
          engine: option.engine,
          family: option.family
        })),
        ...(truncated
          ? {
              hint: `Son ${match.options.length} versiones y la tarjeta muestra ${options.length}: pide el año o el motor para acotar.`
            }
          : {})
      })
    };
  }
};
